import { redirect } from "@tanstack/react-router";
import { createMongoAbility, type RawRuleOf } from "@casl/ability";
import type { User } from "@/lib/api/user";
import { hasPrivilegedAccessFromUser } from "@/lib/casl/privileged-access";
import type { Action, AppAbility, Resource } from "./rbac";

/**
 * Build a CASL ability from the router context user.
 * Safe to call with null user — returns an empty ability (no permissions).
 */
export function buildAbilityFromContext(user: User | null): AppAbility {
  return createMongoAbility<AppAbility>(
    (user?.abilities ?? []) as RawRuleOf<AppAbility>[],
  );
}

/**
 * Route guard: requires the user to have a specific permission.
 * Use in TanStack Router `beforeLoad` hooks.
 *
 * @example
 * beforeLoad: ({ context }) => {
 *   requirePermission(context.user, 'read', 'escalations');
 * }
 */
export function requirePermission(
  user: User | null,
  action: Action,
  subject: Resource,
  redirectTo: string = "/",
): void {
  const ability = buildAbilityFromContext(user);
  if (!ability.can(action, subject)) {
    throw redirect({ to: redirectTo });
  }
}

/**
 * Route guard: requires superadmin (manage:all) access.
 * Shorthand for the most common admin-only guard.
 *
 * @example
 * beforeLoad: ({ context }) => {
 *   requireSuperadmin(context.user);
 * }
 */
export function requireSuperadmin(
  user: User | null,
  redirectTo: string = "/",
): void {
  requirePermission(user, "manage", "all", redirectTo);
}

export function requirePrivilegedAccess(
  user: User | null,
  redirectTo: string = "/",
): void {
  if (!hasPrivilegedAccessFromUser(user)) {
    throw redirect({ to: redirectTo });
  }
}
